import React, { useContext } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { MBUserImage } from '@shared/components/image';

interface Props {
  users: MBAPI.ShortUser[];
}

const styles = StyleSheet.create({
  border: {
    borderColor: 'white',
  },
  userBack: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  userFront: {
    position: 'absolute',
    left: 0,
    bottom: 0,
  },
});

const detailsAndFormStyles = StyleSheet.create({
  container: {
    width: 40,
    height: 40,
  },
  content: {
    width: 30,
    height: 30,
  },
  contentBorderRadius: {
    borderRadius: 15,
    overflow: 'hidden',
  },
  borderWidth: {
    borderWidth: 2,
  },
  smallUser: {
    width: 20,
    height: 20,
    borderRadius: 10,
    overflow: 'hidden',
  },
});

const listStyles: typeof detailsAndFormStyles = StyleSheet.create({
  container: {
    width: 30,
    height: 30,
  },
  content: {
    width: 24,
    height: 24,
  },
  contentBorderRadius: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  borderWidth: {
    borderWidth: 2,
  },
  smallUser: {
    width: 16,
    height: 16,
    borderRadius: 8,
    overflow: 'hidden',
  },
});

const calendarStyles: typeof detailsAndFormStyles = StyleSheet.create({
  container: {
    width: 20,
    height: 20,
  },
  content: {
    width: 16,
    height: 16,
  },
  contentBorderRadius: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  borderWidth: {
    borderWidth: 1,
  },
  smallUser: {
    width: 12,
    height: 12,
    borderRadius: 6,
    overflow: 'hidden',
  },
});

export function MBAssigneeUserIcon({ users }: Props) {
  const renderContext = useContext(CardRenderContext);
  const contextSpecificStyle = (() => {
    if (renderContext === 'calendar') {
      return calendarStyles;
    }
    if (['list', 'taskList', 'agenda', 'table'].includes(renderContext)) {
      return listStyles;
    }
    return detailsAndFormStyles;
  })();

  if (users.length === 1) {
    return (
      <MBView center style={[contextSpecificStyle.container]}>
        <MBView
          style={[
            contextSpecificStyle.content,
            contextSpecificStyle.contentBorderRadius,
            contextSpecificStyle.borderWidth,
            styles.border,
          ]}
        >
          <MBUserImage user={users[0]} size={renderContext === 'calendar' ? 'tiny' : 'small'} />
        </MBView>
      </MBView>
    );
  }

  const otherUsers = users.filter(u => !u.isMe);
  const me = users.find(u => u.isMe);
  // If current user is among assignees, always show that on top
  const front = !!me ? me : users[0];
  const back = !!me ? otherUsers[0] : users[1];

  return (
    <MBView center style={contextSpecificStyle.container}>
      <MBView style={contextSpecificStyle.content}>
        <MBView
          style={[
            contextSpecificStyle.smallUser,
            contextSpecificStyle.borderWidth,
            styles.border,
            styles.userBack,
          ]}
        >
          <MBUserImage size="tiny" user={back} />
        </MBView>
        <MBView
          style={[
            contextSpecificStyle.smallUser,
            contextSpecificStyle.borderWidth,
            styles.border,
            styles.userFront,
          ]}
        >
          <MBUserImage size="tiny" user={front} />
        </MBView>
      </MBView>
    </MBView>
  );
}
